import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  Modal,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, TrendingUp, TrendingDown, AttachMoney, Leaderboard, Calculate } from '@mui/icons-material';

const formatCurrency = (value) => {
  if (typeof value !== 'number') return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value) => {
  if (typeof value !== 'number' || isNaN(value)) return '0,00%';
  return `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%`.replace('.', ',');
};

const getPercentualColor = (value) => {
  if (value >= 0) return 'success';
  return 'error';
};

const KpiModalCard = ({ titulo, valor, icon: Icon, cor }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, p: 1.5, bgcolor: 'background.default', borderRadius: 2 }}>
    {Icon && <Icon sx={{ fontSize: 24, color: `${cor}.main`, mr: 1 }} />}
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{titulo}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: `${cor}.main` }}>{valor}</Typography>
    </Box>
  </Box>
);

const FinanceiroTable = ({ data = [], onRowClick }) => {
  const sortedData = [...data].sort((a, b) => {
    const percA = a.previsto > 0 ? (a.realizado / a.previsto) - 1 : -Infinity;
    const percB = b.previsto > 0 ? (b.realizado / b.previsto) - 1 : -Infinity;
    return percA - percB;
  });

  const totalPrevisto = sortedData.reduce((acc, item) => acc + (item.previsto || 0), 0);
  const totalRealizado = sortedData.reduce((acc, item) => acc + (item.realizado || 0), 0);
  const desvioTotal = totalPrevisto > 0 ? (totalRealizado / totalPrevisto) - 1 : 0;
  const tituloDesvio = desvioTotal >= 0 ? 'Superavit' : 'Desvio';

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 700 }}>Contrato</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Previsto</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Realizado</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.length > 0 ? (
            sortedData.map((row, index) => {
              const percentual = row.previsto > 0 ? (row.realizado / row.previsto) - 1 : 0;
              return (
                <TableRow 
                  key={row.contrato || index} 
                  hover 
                  onClick={() => onRowClick && onRowClick(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  <TableCell>{row.contrato}</TableCell>
                  <TableCell align="center">{formatCurrency(row.previsto)}</TableCell>
                  <TableCell align="center">{formatCurrency(row.realizado)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={formatPercent(percentual)}
                      color={getPercentualColor(percentual)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Typography color="text.secondary" sx={{ py: 4 }}>
                  Nenhum dado financeiro a ser exibido.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {sortedData.length > 0 && (
            <TableRow sx={{ bgcolor: 'grey.800', '& td': { color: '#fff' } }}>
              <TableCell sx={{ fontWeight: 700, color: '#fff' }}>
                {sortedData.length} Contrato{sortedData.length !== 1 ? 's' : ''}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#fff' }} align="center">{formatCurrency(totalPrevisto)}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#fff' }} align="center">{formatCurrency(totalRealizado)}</TableCell>
              <TableCell align="center">
                <Chip
                  label={formatPercent(desvioTotal)}
                  color={getPercentualColor(desvioTotal)}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const FinanceiroModal = ({ open, onClose, contrato, data }) => {
  if (!data || data.length === 0) return null;
  
  const item = data.find(d => d.contrato === contrato);
  if (!item) return null;

  const percentual = item.previsto > 0 ? (item.realizado / item.previsto) - 1 : 0;
  const diferenca = item.realizado - item.previsto;
  const tituloDesvio = percentual >= 0 ? 'Superavit' : 'Desvio';

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 400, bgcolor: 'background.paper', borderRadius: 2, p: 3, boxShadow: 24,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{item.contrato}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
        
        <KpiModalCard titulo="Previsto" valor={formatCurrency(item.previsto)} icon={Calculate} cor="primary" />
        <KpiModalCard titulo="Realizado" valor={formatCurrency(item.realizado)} icon={AttachMoney} cor="secondary" />
        <KpiModalCard titulo="Diferença" valor={formatCurrency(diferenca)} icon={diferenca >= 0 ? TrendingUp : TrendingDown} cor={diferenca >= 0 ? 'success' : 'error'} />
        <KpiModalCard titulo={tituloDesvio} valor={formatPercent(percentual)} icon={percentual >= 0 ? TrendingUp : TrendingDown} cor={percentual >= 0 ? 'success' : 'error'} />
      </Box>
    </Modal>
  );
};

export default FinanceiroTable;